import { Card, Typography, Space, Tag, Popover, Grid } from 'antd'
import {
  ToolOutlined,
  RobotOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { useEffect, useRef, useState } from 'react'
import { formatDateTime, isSubscriptionGeneratedTask } from '../../utils/helpers'
import { RESEARCH_DEPTH_OPTIONS, PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from '../../utils/constants'
import './ProcessingIndicator.css'

const { Text } = Typography

const StatsSidebar = ({ task, stats, isProcessing }) => {
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [animatingStats, setAnimatingStats] = useState({
    toolCalls: false,
    llmCalls: false,
    reports: false,
  })
  const prevStatsRef = useRef(stats)
  const paymentStatus = (task?.paymentStatus || task?.payment_status || '').toUpperCase()
  const billingAmountValue = task?.billingAmount ?? task?.billing_amount
  const billingCurrency = task?.billingCurrency || task?.billing_currency || 'USD'
  const isSubscriptionTask = isSubscriptionGeneratedTask(task)
  const subscriptionMonthlyAmount = task?.subscriptionMonthlyAmount ?? task?.subscription_monthly_amount
  const subscriptionMonthlyCurrency = task?.subscriptionMonthlyCurrency || task?.subscription_monthly_currency || billingCurrency
  const hasPaidAmount =
    !isSubscriptionTask && billingAmountValue != null && billingAmountValue > 0 && paymentStatus === 'PAID'

  const formatBillingAmount = (amount, currency) => {
    if (amount === undefined || amount === null) return null
    const numeric = typeof amount === 'number' ? amount : Number(amount)
    if (Number.isNaN(numeric)) return null
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    })
    return formatter.format(numeric)
  }

  const subscriptionMonthlyLabel = formatBillingAmount(subscriptionMonthlyAmount, subscriptionMonthlyCurrency)
  const subscriptionBillingLabel = subscriptionMonthlyLabel ? `${subscriptionMonthlyLabel} / mo` : 'Subscription'

  const renderPricingDetails = () => {
    const depthValue = task?.researchDepth ?? task?.research_depth
    const depthOption = RESEARCH_DEPTH_OPTIONS.find((option) => option.value === depthValue)
    const analysts = task?.selectedAnalysts ?? task?.selected_analysts ?? []
    const pricingBreakdown = task?.pricingBreakdown

    return (
      <div style={{
        maxWidth: 280,
        padding: '16px',
        background: 'rgba(17, 24, 39, 0.95)',
        backdropFilter: 'blur(12px)',
        borderRadius: 12,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}>
        <div style={{ marginBottom: 14 }}>
          <Text strong style={{ fontSize: 14, color: '#f9fafb', letterSpacing: '-0.01em' }}>
            💰 Pricing Breakdown
          </Text>
        </div>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>Research Depth</Text>
            <Text strong style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.9)' }}>
              {depthOption?.label || 'N/A'}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>Analysts Selected</Text>
            <Text strong style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.9)' }}>
              {analysts.length}
            </Text>
          </div>
          {pricingBreakdown && (
            <>
              <div style={{
                height: 1,
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                margin: '4px 0'
              }} />
              <div>
                <Text style={{
                  fontSize: 11,
                  color: 'rgba(255, 255, 255, 0.5)',
                  display: 'block',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Calculation
                </Text>
                <div style={{
                  fontSize: 11,
                  color: '#a5b4fc',
                  fontFamily: 'SF Mono, Monaco, Courier New, monospace',
                  background: 'rgba(99, 102, 241, 0.15)',
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  lineHeight: 1.5
                }}>
                  {pricingBreakdown.calculationFormula ||
                    `$${pricingBreakdown.basePrice} × ${pricingBreakdown.researchDepthFactor} × ${pricingBreakdown.analystFactor}`}
                </div>
              </div>
            </>
          )}
          <div style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            margin: '4px 0'
          }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0 4px'
          }}>
            <Text strong style={{ fontSize: 13, color: '#f9fafb' }}>Total Amount</Text>
            <Text strong style={{
              fontSize: 16,
              color: '#a5b4fc',
              letterSpacing: '-0.02em',
              textShadow: '0 0 20px rgba(165, 180, 252, 0.3)'
            }}>
              {formatBillingAmount(billingAmountValue, billingCurrency)}
            </Text>
          </div>
        </Space>
      </div>
    )
  }

  useEffect(() => {
    const prevStats = prevStatsRef.current
    const nextAnimating = {}

    if (prevStats.toolCalls !== stats.toolCalls) {
      nextAnimating.toolCalls = true
    }
    if (prevStats.llmCalls !== stats.llmCalls) {
      nextAnimating.llmCalls = true
    }
    if (prevStats.reports !== stats.reports) {
      nextAnimating.reports = true
    }

    if (Object.keys(nextAnimating).length > 0) {
      setAnimatingStats(nextAnimating)
      setTimeout(() => {
        setAnimatingStats({
          toolCalls: false,
          llmCalls: false,
          reports: false,
        })
      }, 600)
    }

    prevStatsRef.current = stats
  }, [stats])

  const containerStyle = isMobile ? { marginTop: 16 } : { position: 'sticky', top: 24 }
  const cardShadow = isMobile ? '0 12px 28px rgba(15, 23, 42, 0.08)' : '0 6px 18px rgba(99, 102, 241, 0.12)'
  const cardBorder = isMobile ? '1px solid #e2e8f0' : 'none'
  const cardRadius = isMobile ? 16 : 12

  const metricItems = [
    {
      key: 'toolCalls',
      icon: <ToolOutlined style={{ fontSize: 14, color: '#667eea' }} />,
      label: 'Tool Calls',
      value: stats.toolCalls || 0,
      color: '#667eea',
      animate: animatingStats.toolCalls,
    },
    {
      key: 'llmCalls',
      icon: <RobotOutlined style={{ fontSize: 14, color: '#8b5cf6' }} />,
      label: 'LLM Calls',
      value: stats.llmCalls || 0,
      color: '#8b5cf6',
      animate: animatingStats.llmCalls,
    },
    {
      key: 'reports',
      icon: <FileTextOutlined style={{ fontSize: 14, color: '#10b981' }} />,
      label: 'Reports',
      value: stats.reports || 0,
      color: '#10b981',
      animate: animatingStats.reports,
    },
  ]

  const metricsContainerStyle = isMobile
    ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }
    : { display: 'flex', flexDirection: 'column', gap: 8 }

  const metricCardStyle = (color, animate) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '10px 12px' : '8px 10px',
    borderRadius: 12,
    border: isMobile ? '1px solid #e2e8f0' : 'none',
    background: isMobile ? '#f8fafc' : 'transparent',
    boxShadow: animate ? '0 0 0 3px rgba(99, 102, 241, 0.12)' : 'none',
    transition: 'box-shadow 0.3s ease',
  })

  const detailRows = [
    {
      key: 'depth',
      label: 'Research Depth',
      value: (() => {
        const depth = task?.researchDepth || task?.research_depth
        const depthOption = RESEARCH_DEPTH_OPTIONS.find(option => option.value === depth)
        return depthOption ? depthOption.label : 'N/A'
      })(),
    },
    {
      key: 'created',
      label: 'Created',
      value: task?.createdAt
        ? formatDateTime(task.createdAt)
        : task?.created_at
        ? formatDateTime(task.created_at)
        : 'N/A',
      icon: <CalendarOutlined style={{ fontSize: 12, color: '#6366f1' }} />,
    },
    {
      key: 'completed',
      label: 'Completed',
      value: task?.completedAt
        ? formatDateTime(task.completedAt)
        : task?.completed_at
        ? formatDateTime(task.completed_at)
        : isProcessing
          ? <Text type="secondary" italic style={{ fontSize: 11 }}>In Progress...</Text>
          : 'N/A',
      icon: <ClockCircleOutlined style={{ fontSize: 12, color: '#6366f1' }} />,
    },
  ]

  if (paymentStatus) {
    detailRows.push({
      key: 'payment',
      label: 'Payment Status',
      value: (
        <Space size={6}>
          <Tag color={PAYMENT_STATUS_COLORS[paymentStatus] || 'default'} style={{ margin: 0 }}>
            {PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus}
          </Tag>
          {paymentStatus !== 'FREE' && (
            <Text style={{ fontSize: 11, color: '#374151' }}>
              {isSubscriptionTask
                ? subscriptionBillingLabel
                : billingAmountValue != null
                  ? `${Number(billingAmountValue).toFixed(2)} ${billingCurrency}`
                  : 'Pending'}
            </Text>
          )}
        </Space>
      ),
    })
  }

  if ((task?.selectedAnalysts || task?.selected_analysts || []).length > 0) {
    detailRows.push({
      key: 'analysts',
      label: 'Analysts',
      value: (
        <Space wrap size={[4, 4]}>
          {(task?.selectedAnalysts || task?.selected_analysts || []).map((analyst, idx) => (
            <Tag key={idx} color="blue" style={{ margin: 0, fontSize: 10, padding: '2px 6px' }}>
              {analyst}
            </Tag>
          ))}
        </Space>
      ),
    })
  }

  return (
    <div style={containerStyle}>
      {(hasPaidAmount || isSubscriptionTask) && (
        <div style={{
          background: isMobile ? '#eef2ff' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: isMobile ? 16 : 10,
          padding: isMobile ? '14px 16px' : '12px 14px',
          marginBottom: 12,
          boxShadow: isMobile ? '0 10px 24px rgba(79, 70, 229, 0.16)' : '0 2px 8px rgba(99, 102, 241, 0.15)',
          border: isMobile ? '1px solid rgba(99, 102, 241, 0.2)' : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Text style={{ fontSize: 11, color: isMobile ? '#4338ca' : 'rgba(255, 255, 255, 0.8)', display: 'block', marginBottom: 2 }}>
                {isSubscriptionTask ? 'Billing' : 'Analysis Cost'}
              </Text>
              <Text strong style={{ fontSize: 18, color: isMobile ? '#1f2937' : '#fff', letterSpacing: '-0.02em' }}>
                {isSubscriptionTask ? subscriptionBillingLabel : formatBillingAmount(billingAmountValue, billingCurrency)}
              </Text>
            </div>
            {!isSubscriptionTask && (
              <Popover
                content={renderPricingDetails()}
                title={null}
                trigger="click"
                placement="bottomRight"
                overlayInnerStyle={{
                  padding: 0,
                  background: 'transparent',
                  boxShadow: 'none'
                }}
              >
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: isMobile ? 'rgba(67, 56, 202, 0.12)' : 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isMobile ? 'rgba(67, 56, 202, 0.18)' : 'rgba(255, 255, 255, 0.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isMobile ? 'rgba(67, 56, 202, 0.12)' : 'rgba(255, 255, 255, 0.2)'
                }}
                >
                  <QuestionCircleOutlined style={{ fontSize: 14, color: isMobile ? '#4338ca' : '#fff' }} />
                </div>
              </Popover>
            )}
          </div>
        </div>
      )}

      <Card
        title={<Text strong style={{ fontSize: 13 }}>Live Statistics</Text>}
        style={{
          marginBottom: 12,
          borderRadius: cardRadius,
          boxShadow: cardShadow,
          border: cardBorder,
        }}
        className={isProcessing ? 'shimmer stats-sidebar' : 'stats-sidebar'}
        bodyStyle={{ padding: isMobile ? '14px 16px' : '12px' }}
      >
        <div style={metricsContainerStyle}>
          {metricItems.map((metric) => (
            <div
              key={metric.key}
              className={metric.animate ? 'stat-card-pulse' : ''}
              style={metricCardStyle(metric.color, metric.animate)}
            >
              <Space size={6}>
                {metric.icon}
                <Text style={{ fontSize: 12, color: '#78716c' }}>{metric.label}</Text>
              </Space>
              <Text
                strong
                style={{ fontSize: 18, color: metric.color }}
                className={metric.animate ? 'stat-value-update' : ''}
              >
                {metric.value}
              </Text>
            </div>
          ))}
        </div>
      </Card>

      <Card
        className="stats-sidebar"
        title={<Text strong style={{ fontSize: 13 }}>Task Details</Text>}
        bodyStyle={{ padding: isMobile ? '14px 16px' : '12px' }}
        style={{
          borderRadius: cardRadius,
          boxShadow: cardShadow,
          border: cardBorder,
        }}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {detailRows.map((row, index) => (
            <div
              key={row.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '6px 0',
                borderBottom: index === detailRows.length - 1 ? 'none' : '1px solid #f1f5f9',
              }}
            >
              <Space size={6}>
                {row.icon}
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  {row.label}
                </Text>
              </Space>
              <div style={{ fontSize: 11, textAlign: 'right', color: '#1f2937' }}>
                {row.value}
              </div>
            </div>
          ))}
        </Space>
      </Card>
    </div>
  )
}

export default StatsSidebar
